import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useLocalStorage } from '@vueuse/core'
import {
  CountryId,
  Currency,
  Country,
  ElectronicPaymentType,
  Language,
  LanguageId,
  type GuestCategoryId,
} from '@previo/codebooks'
import dayjs from 'dayjs'
import {
  PropertyId as PropertyIdProperty,
  CountryId as CountryIdProperty,
  Context,
  LanguageId as LanguageIdProperty,
  TestHotelFlag as TestHotelFlagProperty,
  useFeatureFlags,
} from '@previo/feature-flags'
import { StoreKey } from '~/store/StoreKey'
import { useAPI } from '~/http'
import { LocalStorageKeyManager, LocalStorageKeys } from '~/store/LocalStorageKeyManager'
import { initPropertyRealtime } from '~/composables/usePropertyRealtime'
import type { SelectedProperty } from 'src/store/property/property/types'
import type { PropertyDetailResponse } from '~/http/property/types'
import type { TResponseBankAccount } from '~/pages/invoicing/payment/store/bankAccounts/types'
import { BankAccount } from '~/components/property/store/models/BankAccount'

export const usePropertyStore = defineStore(StoreKey.PROPERTY, () => {
  const loading = ref<boolean>(false)
  const isLoaded = ref<boolean>(false)
  const property = ref<SelectedProperty>({} as SelectedProperty)
  const lastSelected = useLocalStorage<number | null>(
    LocalStorageKeyManager.createStorageKey(LocalStorageKeys.LAST_SELECTED_PROPERTY_ID),
    null,
  )

  const load = (propertyId: number): Promise<void> => {
    loading.value = true

    return useAPI()
      .propertyAPI.loadProperty(propertyId)
      .then((r: PropertyDetailResponse) => {
        const p = {
          name: r.name,
          propertyId: r.id,
          realtimeChannel: r.realtimeChannel,
          defaultCheckInFromTime: r.defaultCheckInFromTime,
          defaultCheckOutTime: r.defaultCheckOutTime,
          guestCategoryId: r.guestCategoryId,
          ratePlanId: r.ratePlanId,
          mealRatePlanId: r.mealRatePlanId,
          mealSettings: r.mealSettings,
          calculatePaymentStatus: r.calculatePaymentStatus,
          sellItemsNotInStock: r.sellItemsNotInStock,
          timezone: r.timezone,
          confirmAccountRecalculation: r.confirmAccountRecalculation,
          vatPayer: r.vatPayer,
          autoRecalculateManualReservations: r.autoRecalculateManualReservations,
          usesDifferentGuestTerm: r.usesDifferentGuestTerm,
          isTest: r.isTest,
          reservationStatusId: r.reservationStatusId,
          usesFloating: r.usesFloating,
          active: r.active,
          contact: r.contact,
          connectedRoomsAutomaticClosing: r.connectedRoomsAutomaticClosing,
          terminalPaymentPopUpMode: r.terminalPaymentPopUpMode,
        }

        const bankAccounts = <BankAccount[]>[]
        r.bankAccounts.forEach((bankAccount: TResponseBankAccount) => {
          bankAccounts.push(
            new BankAccount(
              bankAccount.id,
              bankAccount.account.name,
              Currency.getById(bankAccount.currency),
              bankAccount.account.number,
              bankAccount.account.bankCode,
              bankAccount.account.swift,
              bankAccount.autoImportValid,
            ),
          )
        })

        const languageUsage = r.languageUsage.map((languageId: LanguageId) =>
          Language.getById(languageId),
        )
        const country = Country.getById(r.countryId)
        const currency = Currency.getById(r.currencyId)
        const invoicingCurrency = Currency.getById(r.invoicingCurrencyId)
        const language = Language.getById(r.languageId)
        const allowedCurrencies: Currency[] = r.allowedCurrencies.map((c) => Currency.getById(c))
        const allowedElectronicPaymentTypes: ElectronicPaymentType[] =
          r.allowedElectronicPaymentTypes.map((c) => ElectronicPaymentType.getById(c))
        const generalSettings = r.generalSettings
        const reservationOptionSettings = r.reservationOptionSettings

        property.value = {
          ...p,
          country: country,
          currency: currency,
          invoicingCurrency: invoicingCurrency,
          language: language,
          allowedCurrencies: allowedCurrencies,
          allowedElectronicPaymentTypes: allowedElectronicPaymentTypes,
          reservationOptionSettings: reservationOptionSettings,
          generalSettings: generalSettings,
          languageUsage: languageUsage,
          bankAccounts: bankAccounts,
        }

        dayjs.tz.setDefault(tz())

        useFeatureFlags().appendContext(
          new Context([
            new PropertyIdProperty(property.value.propertyId),
            new CountryIdProperty(property.value.country.id),
            new LanguageIdProperty(property.value.language.id),
            new TestHotelFlagProperty(property.value.isTest),
          ]),
        )

        initPropertyRealtime(property.value.realtimeChannel)

        isLoaded.value = true
      })
      .finally(() => {
        loading.value = false
      })
  }

  const checkInFromHours = (): number => {
    return Number(property.value.defaultCheckInFromTime.split(':')[0])
  }

  const checkInFromMinutes = (): number => {
    return Number(property.value.defaultCheckInFromTime.split(':')[1])
  }

  const checkOutHours = (): number => {
    return Number(property.value.defaultCheckOutTime.split(':')[0])
  }

  const checkOutMinutes = (): number => {
    return Number(property.value.defaultCheckOutTime.split(':')[1])
  }

  const getCountry = (): Country => {
    return property.value.country
  }

  const getCountryId = (): CountryId => {
    return property.value.country.id
  }

  const isVatPayer = (): boolean => {
    return property.value.vatPayer
  }

  const isCzOrSk = (): boolean => {
    return [CountryId.CZE, CountryId.SVK].includes(getCountryId())
  }

  const getCurrency = (): Currency => {
    return property.value.currency
  }

  const getDefaultGuestCategoryId = (): GuestCategoryId => {
    return property.value.guestCategoryId
  }

  const usesAccountRecalculationConfirmation = (): boolean => {
    return property.value.confirmAccountRecalculation
  }

  const usesManualReservationAccountRecalculation = (): boolean => {
    return property.value.autoRecalculateManualReservations
  }

  const getDefaultMealRatePlanId = (): number => {
    return property.value.mealRatePlanId
  }

  const usesDifferentGuestTerm = (): boolean => {
    return property.value.usesDifferentGuestTerm
  }

  const getLanguage = (): Language => {
    return property.value.language
  }

  const getMostCommonGuestLanguage = (): Language => {
    return property.value.languageUsage[0]
  }

  const getAllowedCurrencies = (): Currency[] => {
    return property.value.allowedCurrencies
  }

  const hasSingleCurrency = (): boolean => {
    return property.value.allowedCurrencies.length === 1
  }

  const tz = (): string => {
    return property.value.timezone
  }

  const usesConnectedRoomsAutomaticClosing = (): boolean => {
    return property.value.connectedRoomsAutomaticClosing
  }

  const setConnectedRoomsAutomaticClosing = (automaticClosing: boolean): void => {
    property.value.connectedRoomsAutomaticClosing = automaticClosing
  }

  const isLoading = computed(() => {
    return loading.value && isLoaded.value !== true
  })

  return {
    property,
    lastSelected,
    load,
    isLoading,
    isLoaded,
    checkInFromHours,
    checkInFromMinutes,
    checkOutHours,
    checkOutMinutes,
    getCountry,
    getCountryId,
    isVatPayer,
    isCzOrSk,
    getCurrency,
    getDefaultGuestCategoryId,
    getDefaultMealRatePlanId,
    usesAccountRecalculationConfirmation,
    usesManualReservationAccountRecalculation,
    getLanguage,
    getMostCommonGuestLanguage,
    getAllowedCurrencies,
    hasSingleCurrency,
    usesDifferentGuestTerm,
    usesConnectedRoomsAutomaticClosing,
    setConnectedRoomsAutomaticClosing,
    tz,
  }
})
